import React, { useState, useEffect } from 'react';
import { Customer } from '@/hooks/useCustomers';
import { BulkUploadModal } from './BulkUploadModal';
import { useSourceManager } from '@/hooks/useSourceManager';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface BulkUploadSourceCardProps {
  customer: Customer;
  onSetupMode?: (mode: string) => void;
}

export const BulkUploadSourceCard: React.FC<BulkUploadSourceCardProps> = ({ customer }) => {
  const { sources, enableSource, disableSource, loading } = useSourceManager(customer.id);
  const [showUploader, setShowUploader] = useState(false);
  const [batchStats, setBatchStats] = useState({ recentUploads: 0, totalFilesProcessed: 0 });

  const bulkSource = sources.bulk;
  const isActive = bulkSource?.status === 'active';

  // Load batch statistics
  useEffect(() => {
    const loadBatchStats = async () => {
      const { data: batches } = await supabase
        .from('bulk_upload_batches')
        .select('*')
        .eq('customer_id', customer.id);

      if (batches) {
        const recentUploads = batches.filter(batch => 
          new Date(batch.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        ).length;
        
        const totalFilesProcessed = batches.reduce((sum, batch) => sum + batch.processed_files, 0);
        
        setBatchStats({ recentUploads, totalFilesProcessed });
      }
    };

    if (customer.id) {
      loadBatchStats();
    }
  }, [customer.id]);

  const handleToggle = async () => {
    if (isActive) {
      await disableSource('bulk');
    } else {
      await enableSource('bulk', { auto_enabled: true });
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
            <span className="text-xl">📁</span>
          </div>
          <div>
            <h3 className="font-medium text-foreground">Bulk Upload</h3>
            <p className="text-sm text-muted-foreground">Manual file uploads and batch processing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "✅ Active" : "⏸️ Inactive"}
          </Badge>
          <Button
            variant={isActive ? "outline" : "default"}
            size="sm"
            onClick={handleToggle}
            disabled={loading}
          >
            {isActive ? "Disable" : "Enable"}
          </Button>
          <Button 
            onClick={() => setShowUploader(true)}
            disabled={!isActive}
          >
            📁 Upload Bills
          </Button>
        </div>
      </div>

      {isActive && (
        <>
          {/* Upload Stats */}
          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-green-500/5 rounded">
            <div>
              <div className="text-xs text-green-600">Recent Uploads</div>
              <div className="text-lg font-semibold text-green-700">{batchStats.recentUploads}</div>
              <div className="text-xs text-green-600/80">This week</div>
            </div>
            <div>
              <div className="text-xs text-green-600">Total Processed</div>
              <div className="text-lg font-semibold text-green-700">{batchStats.totalFilesProcessed}</div>
              <div className="text-xs text-green-600/80">All time</div>
            </div>
          </div>

      {/* Upload Guidelines */}
      <div className="p-3 bg-muted rounded mb-4">
        <div className="text-xs font-medium text-foreground mb-2">📋 Upload Guidelines</div>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• 📄 Supported formats: PDF, JPG, PNG</li>
          <li>• 📦 Maximum file size: 25MB per file</li>
          <li>• 🗂️ Batch upload: Up to 20 files at once</li>
          <li>• 🤖 Automatic AI processing and data extraction</li>
          <li>• ⚡ Processing time: 1-3 minutes per file</li>
        </ul>
      </div>

      {/* Recent Activity */}
      <div className="mb-4">
        <div className="text-xs font-medium text-foreground mb-2">📊 Recent Activity</div>
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm p-2 bg-primary/5 rounded">
            <span>📄 3 files uploaded</span>
            <span className="text-xs text-muted-foreground">2 hours ago</span>
          </div>
          <div className="flex justify-between items-center text-sm p-2 bg-green-500/5 rounded">
            <span>✅ 2 bills processed successfully</span>
            <span className="text-xs text-muted-foreground">1 day ago</span>
          </div>
        </div>
      </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={() => setShowUploader(true)}>
              📁 Upload Files
            </Button>
            <Button variant="outline">
              📊 View History
            </Button>
            <Button variant="outline">
              ⚙️ Settings
            </Button>
          </div>
        </>
      )}

      {!isActive && (
        <div className="p-3 bg-muted rounded text-center">
          <div className="text-sm text-muted-foreground mb-2">
            Enable bulk upload to start processing multiple files at once
          </div>
          <Button onClick={handleToggle} disabled={loading}>
            {loading ? "Enabling..." : "Enable Bulk Upload"}
          </Button>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showUploader && (
        <BulkUploadModal
          customer={customer}
          onClose={() => setShowUploader(false)}
          onUploadComplete={(files) => {
            console.log('Upload completed:', files);
            setShowUploader(false);
          }}
        />
      )}
    </div>
  );
};